import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Bookmark, Heart, Check, ChevronDown, List } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserLibrary, type MediaEnrichment } from '../contexts/UserLibraryContext';
import { useLists } from '../contexts/ListsContext';
import { useToast } from './ui/Toast';
import { CreateListModal, type MediaFilter } from './CreateListModal';
import { SocialUpgradeModal } from './ui/SocialUpgradeModal';
import { useCustomListMembershipBatch } from '../hooks/useListsQuery';
import { effectiveTier } from '../utils/entitlements';
import { ApiError } from '../lib/api';

// Position constants
const TOPBAR_H = 64;
const PAD = 8;
const MENU_W = 256; // w-64 = 16rem = 256px

interface AddToListDropdownProps {
  mediaId: number;
  mediaType: 'movie' | 'tv';
  mediaTitle: string;
  mediaPoster?: string;
  mediaYear?: number;
  className?: string;
  // TASK A: Enrichment fields for instant metadata display on WatchlistPage
  enrichment?: MediaEnrichment;
}

export function AddToListDropdown({
  mediaId,
  mediaType,
  mediaTitle,
  mediaPoster,
  mediaYear,
  className = '',
  enrichment,
}: AddToListDropdownProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<string | null>(null);
  // TASK D: Removed isItemPending - no grey pending UX
  // TASK 1: Use isInList from ListsContext as primary source (reads optimistic React Query cache)
  // Phase 5: Use setListItemDesired for SPAM-SAFE custom list operations
  const { lists: watchlists, createWatchlist, setListItemDesired, isInList: optimisticIsInList } = useLists();

  // Default list membership and toggles from UserLibraryContext
  const {
    isInWatchlist,
    isInFavorites,
    toggleWatchlist,
    toggleFavorites,
    toggleCustomList,
  } = useUserLibrary();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get all status from optimistic UI context (single source of truth)
  const watchlistStatus = isInWatchlist(mediaId, mediaType);
  const favoritesStatus = isInFavorites(mediaId, mediaType);

  // Get custom lists from watchlists hook (has full metadata)
  const customLists = watchlists.filter(
    (w) =>
      !w.is_default &&
      (!w.media_filter || w.media_filter === 'mixed' || w.media_filter === mediaType)
  );

  // Batch membership check for custom lists (10M scale - single query for cold cache fallback)
  const customListIds = useMemo(() => customLists.map((l) => l.id), [customLists]);
  const { isInList: serverIsInList, inListIds: serverInListIds } = useCustomListMembershipBatch(
    mediaId,
    mediaType,
    customListIds,
    { enabled: isOpen && customListIds.length > 0 }
  );

  // Compute unified isInList - optimistic cache first, server fallback
  // This ensures UI state matches what the mutation will read, preventing wrong-branch toggles
  const isInCustomList = useCallback((listId: string) => {
    // Optimistic cache is primary (matches what toggleListItem reads)
    const optimistic = optimisticIsInList(listId, mediaId, mediaType);
    // Server membership is fallback for cold cache
    const server = serverIsInList(listId);
    return optimistic || server;
  }, [optimisticIsInList, mediaId, mediaType, serverIsInList]);

  // TASK 1: Compute inListIds from both sources for button styling
  const inListIds = useMemo(() => {
    const combined = new Set<string>();
    // Add from optimistic cache
    customListIds.forEach(listId => {
      if (optimisticIsInList(listId, mediaId, mediaType)) {
        combined.add(listId);
      }
    });
    // Add from server (fallback)
    serverInListIds.forEach(id => combined.add(id));
    return combined;
  }, [customListIds, optimisticIsInList, mediaId, mediaType, serverInListIds]);

  // Update menu position - always below trigger, clamp to viewport
  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    // If trigger is hidden behind TopBar or below viewport, close menu
    if (rect.bottom < TOPBAR_H || rect.top > window.innerHeight) {
      setIsOpen(false);
      return;
    }

    const menuH = menuRef.current.offsetHeight || 300;

    // Align left edge with button left, clamp to viewport
    let left = rect.left;
    left = Math.max(PAD, Math.min(left, window.innerWidth - MENU_W - PAD));

    // Always position below trigger
    let top = rect.bottom + PAD;

    // Clamp to viewport (don't go below bottom, don't go above TopBar)
    top = Math.min(top, window.innerHeight - menuH - PAD);
    top = Math.max(TOPBAR_H + PAD, top);

    // Direct DOM manipulation bypasses React re-renders for smooth scroll tracking
    menuRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    menuRef.current.style.opacity = '1';
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Set initial position synchronously after render (prevents visible jump)
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  // Update position on scroll/resize/sidebar-collapse
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('sidebar-collapse-change', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
        window.removeEventListener('sidebar-collapse-change', updateMenuPosition);
      };
    }
  }, [isOpen, updateMenuPosition]);

  // NOTE: No fetchCustomListItems effect needed - useCustomListMembershipBatch handles membership checks

  if (!user) return null;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Optimistic UI toggle handlers for default lists
  // TASK A: Pass enrichment for instant metadata display on WatchlistPage
  const handleDefaultAction = async (slug: 'watchlist' | 'favorites') => {
    // Capture current state BEFORE toggle for toast message
    const wasInWatchlist = isInWatchlist(mediaId, mediaType);
    const wasInFavorites = isInFavorites(mediaId, mediaType);

    // Use optimistic toggles - they return immediately (UI updates instantly)
    try {
      if (slug === 'watchlist') {
        await toggleWatchlist(mediaId, mediaType, mediaTitle, mediaPoster, mediaYear, enrichment);
        toast.success(wasInWatchlist ? 'Removed from Watchlist' : 'Added to Watchlist');
      } else if (slug === 'favorites') {
        await toggleFavorites(mediaId, mediaType, mediaTitle, mediaPoster, mediaYear, enrichment);
        toast.success(wasInFavorites ? 'Removed from Favorites' : 'Added to Favorites');
      }
    } catch (err) {
      console.error('Error toggling list:', err);
      toast.error('Something went wrong');
    }
  };

  // Phase 5: SPAM-SAFE custom list toggle using setListItemDesired
  // Uses explicit desiredInList (no toggle semantics), per-item abort, last-intent-wins
  // Only shows toast on real state flips (didChange: true)
  const handleCustomListAction = async (listId: string) => {
    // Capture current state to determine desired state
    const wasInList = isInCustomList(listId);
    const listName = customLists.find(l => l.id === listId)?.name || 'list';

    try {
      // SPAM-SAFE: Use explicit desiredInList (inverse of current state)
      // Per-item abort ensures last click wins, no toast spam
      const result = await setListItemDesired(
        listId,
        mediaId,
        mediaType,
        mediaTitle,
        !wasInList, // desiredInList: if was in list, we want out; if not, we want in
        mediaPoster,
        mediaYear,
        enrichment
      );

      // Only show toast on REAL state flips (not superseded/no-op)
      if (result.didChange) {
        toast.success(result.direction === 'added' ? `Added to ${listName}` : `Removed from ${listName}`);
      }
    } catch (err) {
      console.error('Error setting list membership:', err);
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setUpgradeModalFeature('Adding to custom lists');
      } else {
        toast.error('Something went wrong');
      }
    }
    // NOTE: No invalidateMembership here - mutation onSettled handles it (single invalidation point)
  };

  const handleListCreated = (newList: {
    id: string;
    name: string;
    description?: string;
    visibility: string;
    media_filter: MediaFilter;
  }) => {
    // Use optimistic toggleCustomList to add to new list
    toggleCustomList(newList.id, mediaId, mediaType, mediaTitle, mediaPoster, mediaYear);
    setIsOpen(false);
  };

  // Check if in any list for button styling
  const isInAnyCustomList = inListIds.size > 0;
  const isInAnyList = watchlistStatus || favoritesStatus || isInAnyCustomList;

  const dropdownMenu =
    isOpen &&
    createPortal(
      <div
        ref={menuRef}
        className="fixed w-64 rounded-xl border border-teal-500/20 bg-[#0F1419]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden z-40 will-change-transform"
        style={{ top: 0, left: 0, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Teal glow overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 to-transparent pointer-events-none" />

        {/* Watchlist - uses optimistic UI */}
        <button
          onClick={() => handleDefaultAction('watchlist')}
          className={`relative w-full px-4 py-3 flex items-center gap-3 text-left text-sm transition-all duration-150 ${
            watchlistStatus
              ? 'bg-blue-500/20 text-blue-400'
              : 'text-white hover:bg-teal-500/10'
          }`}
        >
          {watchlistStatus ? (
            <Check className="w-4 h-4" />
          ) : (
            <Bookmark className="w-4 h-4" />
          )}
          <span className="flex-1">Watchlist</span>
          {watchlistStatus && <Check className="w-4 h-4 text-blue-400" />}
        </button>

        {/* Favorites - uses optimistic UI */}
        <button
          onClick={() => handleDefaultAction('favorites')}
          className={`relative w-full px-4 py-3 flex items-center gap-3 text-left text-sm transition-all duration-150 ${
            favoritesStatus
              ? 'bg-pink-500/20 text-pink-400'
              : 'text-white hover:bg-teal-500/10'
          }`}
        >
          <Heart className={`w-4 h-4 ${favoritesStatus ? 'fill-current' : ''}`} />
          <span className="flex-1">Favorites</span>
          {favoritesStatus && <Check className="w-4 h-4 text-pink-400" />}
        </button>

        {/* Divider before custom lists */}
        {customLists.length > 0 && <div className="border-t border-white/10" />}

        {/* Custom Lists - uses batch membership hook (single query for all) */}
        {/* TASK D: Removed grey pending UX - buttons always interactive */}
        {customLists.map((list) => {
          const listStatus = isInCustomList(list.id);
          return (
            <button
              key={list.id}
              onClick={() => {
                if (isFree) { setUpgradeModalFeature('Adding to custom lists'); return; }
                handleCustomListAction(list.id);
              }}
              className={`relative w-full px-4 py-3 flex items-center gap-3 text-left text-sm transition-all duration-150 ${
                listStatus
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'text-white hover:bg-teal-500/10'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="flex-1 truncate">{list.name}</span>
              {listStatus && <Check className="w-4 h-4 text-purple-400" />}
            </button>
          );
        })}

        {/* Divider before create new */}
        <div className="border-t border-white/10" />

        {/* Create New List */}
        <button
          onClick={() => {
            if (isFree) { setUpgradeModalFeature('Creating custom lists'); return; }
            setShowCreateListModal(true);
            setIsOpen(false);
          }}
          className="relative w-full px-4 py-3 flex items-center gap-3 text-left text-sm text-teal-400 hover:bg-teal-500/10 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create New List</span>
        </button>
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
          isInAnyList
            ? 'bg-teal-500/20 border border-teal-500/50 text-teal-400 hover:bg-teal-500/30'
            : isOpen
              ? 'bg-[#20242B] border border-teal-500/50 text-white'
              : 'bg-[#1A1F26] border border-teal-500/30 text-slate-300 hover:bg-[#20242B] hover:text-white'
        } ${className}`}
      >
        <Plus size={18} />
        <span>Add to</span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {dropdownMenu}

      <CreateListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onCreated={handleListCreated}
        createWatchlist={createWatchlist}
      />

      <SocialUpgradeModal
        isOpen={!!upgradeModalFeature}
        onClose={() => setUpgradeModalFeature(null)}
        feature={upgradeModalFeature || ''}
      />
    </>
  );
}
